import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import GetSchema, { ISchema } from "../Schema";
import FormRenderer from "./FormRenderer";
import HttpStatusCodes from "../../../../modules/HttpStatusCodes";
import { httpGet, httpPost, ResponseContext } from "../../../../modules/Backend";
import { globalAdditionSignal, globalCanSubmitSignal, globalLoadingSignal, globalSubmittingSignal } from "../../layout/Layout";
import { useSignals } from "@preact/signals-react/runtime";
import { FormMemory } from "./FormMemory";
import { useKeyBind } from "../../../../modules/keybinds/useKeyBind";
import KeyBinds, { KeyActions } from "../../../../modules/keybinds/keyBindList";
import { EntityNames } from "../../../../enums/EntityNames";
import { PropertyTypes } from "../../../../enums/PropertyTypes";
import Size from "../../../../enums/Size";

export interface IForm {
    entityName: string,
    entityId?: string,
    parentEntityName?: string,
    parentId?: string,
    isCreate: boolean,
    isLoading?: boolean | undefined,
    isInline?: boolean | undefined,
    submitOnly?: boolean | undefined,
    disableAddEditEvents?: boolean | undefined,
    clearAfterSubmit?: boolean | undefined,
    onSubmitCallback?: Function,
    preSubmitCallback?: Function,
    submitFunction?: Function,
    onCancel?: Function,
    clearFunc?: (key: string, value: any) => boolean,
    submitNotAllowed?: boolean | undefined,
    preLoadedParentData?: Record<string, any> | undefined,
    formWrapperClassName?: string | undefined,
    formActionsClassName?: string | undefined,
    formButtonsClassName?: string | undefined,
    formButtonSize?: Size | undefined
}

export interface IPreConditionResponse {
    requiresIntervention: boolean
}

export interface IOnComplete {
    elementName: string,
    elementValue: string
}

export type Change = {
    value: string | number | null;
    elementName?: string;
};

function resolveEntityName(entityName: string, parentEntityName: string | undefined) {
    const subEntities = [
        EntityNames.sop,
        EntityNames.certifications,
        EntityNames.manuals,
    ];
    if (subEntities.includes(entityName as EntityNames)) {
        return parentEntityName ?? '';
    }
    return entityName ?? parentEntityName ?? '';
}

function getEmptyDefaultValues(schema: ISchema): Record<string, any> {
    const empty: Record<string, any> = {};
    for (const prop of schema.props ?? []) {
        if (prop.type === PropertyTypes.address) {
            empty[prop.name] = {
                companyStreet: "",
                address1: "",
                address2: "",
                city: "",
                county: "",
                zip: "",
                country: "",
            };
        } else if (prop.type === PropertyTypes.contactDetails) {
            empty[prop.name] = {
                telephone: "",
                mobile: "",
                fax: "",
                email: "",
            };
        } else {
            empty[prop.name] = "";
        }
    }
    return empty;
}

export default function Form(props: IForm) {
    useSignals();
    useKeyBind(KeyBinds[KeyActions.Save], (e: KeyboardEvent) => {
        if (globalCanSubmitSignal.value.allowed) {
            rhfHandleSubmit(onSubmitWithData)(e as any);
        }
    })

    const { entityName, entityId, parentId, parentEntityName, isInline, clearAfterSubmit,
        disableAddEditEvents, submitOnly, onSubmitCallback, submitFunction, preSubmitCallback, onCancel,
        submitNotAllowed, preLoadedParentData, clearFunc, 
        formWrapperClassName, formActionsClassName, formButtonsClassName, formButtonSize
    } = props;
    const resolvedEntityName = resolveEntityName(entityName, parentEntityName);
    const schema: ISchema = GetSchema(resolvedEntityName);
    const formMemory = new FormMemory(schema, resolvedEntityName);
    const [values, setValues] = useState<Record<string, any>>({});
    const [parentValues, setParentValues] = useState<Record<string, any> | undefined>(undefined);
    const [inlineError, setInlineError] = useState<string | undefined>(undefined);
    const [canRender, setCanRender] = useState<boolean>(false);

    const { register, control, handleSubmit: rhfHandleSubmit, watch, reset, setValue } = useForm<Record<string, any>>({
        defaultValues: {},
        mode: "onChange",
    });
    const formValues = watch();

    function clearValues() {
        setValues((prevValues) => {
            if (!clearFunc) {
                const emptyDefaults = getEmptyDefaultValues(schema);
                reset(emptyDefaults);
                return emptyDefaults;
            }
            const newValues: Record<string, any> = {};
            for (const key in prevValues) {
                if (!clearFunc(key, prevValues[key])) {
                    newValues[key] = prevValues[key];
                }
            }
            const merged = { ...getEmptyDefaultValues(schema), ...newValues };
            reset(merged);
            return merged;
        });
    }

    const onChange = (value: string | number, elementName?: string) => {
        setValues({ ...values, [elementName ? elementName : value]: value });
    };

    const onChangeBatched = (changes: Change[]) => {
        const updatedValues = { ...values };
        changes.forEach(({ value, elementName }) => {
            updatedValues[elementName!] = value;
            setValue(elementName!, value);
        });
        setValues(updatedValues);
    };

    const onComplete = (elementArray: IOnComplete[]) => {
        var combinedData: any = {};
        elementArray.forEach(element => {
            Object.assign(combinedData, { [element.elementName]: element.elementValue !== null ? element.elementValue.toString() : element.elementValue });
        });
        setValues({ ...values, ...combinedData });
    };

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Submit hidden form for browser autofill learning (non-blocking)
        const autofillForm = document.getElementById('autofill-form') as HTMLFormElement;
        if (autofillForm && formValues) {
            const allInputs = autofillForm.querySelectorAll('input, textarea') as NodeListOf<HTMLInputElement | HTMLTextAreaElement>;
            allInputs.forEach(input => {
                const key = input.getAttribute('data-field-key');
                if (key) {
                    const keys = key.split('.');
                    let value: any = formValues;
                    for (const k of keys) {
                        value = value?.[k];
                    }
                    const stringValue = value != null ? String(value) : '';
                    input.value = stringValue;
                }
            });
            try {
                autofillForm.submit();
            } catch (e) { }
        }

        rhfHandleSubmit(onSubmitWithData)(event);
    };

    async function onSubmitWithData(data: Record<string, any>) {
        globalCanSubmitSignal.value = { allowed: false };
        globalSubmittingSignal.value = { isBusy: true };

        const allvalues = { ...data };
        for (const property of schema.props ?? {}) {
            if (property.formValueResolver && property.formValueResolver instanceof Function) {
                allvalues[property.name] = property.formValueResolver(props);
            }
        }

        formMemory.persist(allvalues);

        if (entityId) {
            submitFunction
                ? await submitFunction(() => submitEdit(allvalues), data, clearValues)
                : await submitEdit(allvalues);
        } else {
            submitFunction
                ? await submitFunction(() => submitCreate(allvalues), data, clearValues)
                : await submitCreate(allvalues);
        }
    }

    async function submitCreate(allvalues: any) {
        const payload = { ...allvalues, parentId: parentId };
        if (preSubmitCallback) {
            preSubmitCallback();
        }

        const result = await handleCreate(resolvedEntityName!, payload);
        globalCanSubmitSignal.value = { allowed: true };
        globalSubmittingSignal.value = { isBusy: false };
        if (result?.status === HttpStatusCodes.Created || result?.status === HttpStatusCodes.Ok) {
            if (!disableAddEditEvents) {
                globalAdditionSignal.value = { id: result.data.id };
            }
            if (clearAfterSubmit) {
                const emptyDefaults = getEmptyDefaultValues(schema);
                reset(emptyDefaults);
                setValues(emptyDefaults);
            }
            if (onSubmitCallback) {
                onSubmitCallback(result.data);
            }
            if (!isInline) {
                if (schema.onAdd) {
                    // Schema on add often uses full page redirect or reload, ensure that double submissions are not
                    // possible during the reload or redirection
                    globalCanSubmitSignal.value = { allowed: false };
                    globalSubmittingSignal.value = { isBusy: true };
                    schema.onAdd(result.data.id);
                } else {
                    window.history.back();
                }
            }
        }
    }

    async function submitEdit(allvalues: any) {
        const payload = { ...allvalues, id: entityId };
        if (preSubmitCallback) {
            preSubmitCallback();
        }
        const result = await handleUpdate(resolvedEntityName!, payload);
        globalCanSubmitSignal.value = { allowed: true };
        globalSubmittingSignal.value = { isBusy: false };
        if (result?.status === HttpStatusCodes.Ok) {
            if (clearAfterSubmit) {
                const emptyDefaults = getEmptyDefaultValues(schema);
                reset(emptyDefaults);
                setValues(emptyDefaults);
            }
            if (onSubmitCallback) {
                onSubmitCallback(result.data);
            }
            if (isInline) {
                const responseContext = result.headers.get('X-Response-Context');
                if (responseContext === ResponseContext.Success && !disableAddEditEvents) {
                    // Success indicates an immediate update, whereas information is a queued update
                }
            } else {
                window.history.back();
            }
        }
    }

    async function getData() {
        setCanRender(false)
        globalLoadingSignal.value = { isBusy: true };
        if (entityId != null && entityId != "") {
            const result = await httpGet(`api/${resolvedEntityName}/get?id=${entityId}`)
            if (result?.data != undefined) {
                // This is an edit so values from the backend take precedence over form memory
                const payload = result.data;
                setValues(payload);
                reset(payload);
            }
        } else {
            // This is an add, so we defer to form memory for any defaults
            const defaultValues = formMemory.resolve();
            setValues(defaultValues);
            reset(defaultValues);
        }

        // Do we have at least one property that depends on parent data
        if (schema?.props?.some(x => !!x.renderBasedOnParentData)) {
            if (preLoadedParentData) {
                setParentValues(preLoadedParentData);
            } else {
                const result = await httpGet(`api/${parentEntityName}/get?id=${parentId}`)
                if (result?.data != undefined) {
                    setParentValues(result.data);
                }
            }
        }
        setCanRender(true)
        globalLoadingSignal.value = { isBusy: false };
    }

    function hasValue(obj: any) {
        if (!obj)
            return false

        if (Array.isArray(obj) && obj.length === 0)
            return false

        return true
    }

    function validate(valuesToValidate: Record<string, any>) {
        //loop through schema and validate
        var _canSubmit = true;
        const isCreate = !entityId; // Determine if this is a create operation
        schema.props?.forEach(prop => {
            if (_canSubmit === false) {
                return;
            }

            // Skip addOnly properties when not creating
            if (prop.addOnly === true && !isCreate) {
                return;
            }

            // Skip editOnly properties when creating
            if (prop.editOnly === true && isCreate) {
                return;
            }

            if (prop.requiredInFormIf && parentValues) {
                const isRequiredCondition = prop.requiredInFormIf(parentValues);
                if (isRequiredCondition === false) {
                    _canSubmit = true;
                    globalCanSubmitSignal.value = { allowed: true && !submitNotAllowed };
                    return;
                }
            } else {

                if (prop.requiredInForm === true && !hasValue(valuesToValidate[prop.name])) {
                    _canSubmit = false;
                    // Don't return otherwise validation messages don't show up
                }

                //validate subprops first
                if (prop.children) {
                    prop.children.map(childProp => {
                        if (_canSubmit === false) {
                            return;
                        }
                        if (valuesToValidate[prop.name]?.[childProp.name] && childProp.validateRule != undefined && childProp.validateRule != null) {
                            _canSubmit = false;
                            //check if value is in values
                            if (childProp.name in valuesToValidate[prop.name]) {
                                if (!childProp.validateRule(valuesToValidate[prop.name][childProp.name])) {
                                    _canSubmit = false;
                                    return;
                                } else {
                                    _canSubmit = true;
                                    return
                                }
                            }
                        }
                    })
                }

                //validate parent prop
                if (prop.validateRule != undefined && prop.validateRule != null) {
                    _canSubmit = false;
                    //check if value is in values
                    if (prop.name in valuesToValidate) {
                        if (!prop.validateRule(valuesToValidate[prop.name])) {
                            _canSubmit = false;
                            return;
                        } else {
                            _canSubmit = true;
                        }
                    }
                }
            }
        });
        globalCanSubmitSignal.value = { allowed: _canSubmit && !submitNotAllowed };
    }

    async function handleUpdate(entityName: string, payload: any) {

        const errorcallback = (e: string | undefined) => {
            setInlineError(e);
        }
        return await httpPost(`api/${entityName}/update`, payload, schema.httpContentType, errorcallback)
    }

    async function handleCreate(entityName: string, payload: any) {
        return await httpPost(`api/${entityName}/create`, payload, schema.httpContentType, setInlineError)
    }

    function handleCancel() {
        if (onCancel) {
            onCancel();
        }
    }

    useEffect(() => {
        getData();
    }, [entityId])

    useEffect(() => {
        if (canRender) {
            validate(formValues ?? values);
        }
    }, [formValues, values, parentValues, submitNotAllowed, canRender])

    return (<>
        {canRender &&
            <FormRenderer
                formWrapperClassName={formWrapperClassName}
                formActionsClassName={formActionsClassName}
                formButtonsClassName={formButtonsClassName}
                formButtonSize={formButtonSize}
                entityName={resolvedEntityName ? resolvedEntityName : ''}
                parentId={parentId ? parentId : ''}
                parentValues={parentValues}
                values={formValues ?? values}
                onChange={onChange}
                onChangeBatched={onChangeBatched}
                onComplete={onComplete}
                onSubmit={onSubmit}
                schema={schema}
                inlineError={inlineError}
                isCreate={!entityId}
                isInline={isInline}
                submitOnly={submitOnly}
                onCancel={handleCancel}
                register={register}
                control={control}
                setValue={setValue}
            />
        }
        {!canRender &&
            <div className={`display--flex alignItems--center flex__direction--column ${(isInline ? '' : 'gap--s')}`}>
                <div className={`form__wrapper form__wrapper${(isInline ? '--inline' : '')}`}>
                    <div className="loaders loaders__panel form__inline-loader"></div>
                </div>
            </div>
        }
    </>)
}

